import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Clock, AlertTriangle, CheckCircle2, Lock, ArrowUpRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { TrialPassRedeem } from './TierGate';

const TIER_LABEL = { guest: 'Guest', starter: 'Starter', pro: 'Pro', fleet: 'Fleet' };

export default function PlanStatusCard() {
  const { entitlement, isAdmin } = useAuth();
  if (!entitlement) return null;
  const { tier, source, trial, trial_expired, subscription } = entitlement;
  const paid = source === 'subscription';

  return (
    <div className="mi-surface p-6" data-testid="dash-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mi-eyebrow text-[#E63946] flex items-center gap-2"><Sparkles className="w-3 h-3" /> // WRAPULATOR PLAN</div>
          <div className="mi-headline text-4xl mt-2 leading-none" data-testid="dash-plan-tier">
            {TIER_LABEL[tier]}
            <span className="text-base text-white/50 ml-3 font-normal">
              {source === 'admin' && 'Admin · all access'}
              {source === 'comp' && 'Complimentary · granted by Motor iNKS'}
              {paid && (subscription?.status === 'trialing' ? '7-day free trial' : 'Paid · active')}
              {source === 'trial' && trial && `Trial pass · ${trial.days_left} day${trial.days_left === 1 ? '' : 's'} left`}
              {source === 'none' && !trial_expired && 'Free · price ranges only'}
              {trial_expired && 'Trial ended'}
            </span>
          </div>
          <div className="text-white/60 text-xs mt-2 max-w-xl leading-relaxed">
            {paid && subscription?.cancel_at_period_end && <span className="text-yellow-300">Cancels at period end. </span>}
            {paid && subscription?.status === 'trialing' && subscription?.trial_end && <>Your card is charged when the trial ends on {new Date(subscription.trial_end * 1000).toLocaleDateString()}. </>}
            {source === 'trial' && trial && <>Full {TIER_LABEL[trial.tier]} access until {new Date(trial.expires_at).toLocaleDateString()}. Convert to a paid plan before then to keep exact quotes, margin math and PDF proposals.</>}
            {source === 'comp' && <>You have complimentary {TIER_LABEL[tier]} access{entitlement.comp?.expires_at ? ` until ${new Date(entitlement.comp.expires_at).toLocaleDateString()}` : ''} — no card needed.</>}
            {source === 'none' && !trial_expired && <>Guest sees pricing as a range. Starter, Pro and Fleet unlock exact quotes, custom labor, margin preview and branded PDFs.</>}
            {trial_expired && <>Your 30-day trial pass has ended and you're back on Guest. Pick a plan below to restore full access.</>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {source === 'trial' && <span className="mi-tag mi-tag-red text-[9px] inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {trial.days_left} DAYS LEFT</span>}
          {trial_expired && <span className="mi-tag border-yellow-400/50 text-yellow-400 text-[9px] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> TRIAL ENDED</span>}
          {paid && <span className="mi-tag mi-tag-red text-[9px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {subscription.status.toUpperCase()}</span>}
          {!paid && !isAdmin && source !== 'comp' && (
            <Link to="/#membership" data-testid="dash-plan-upgrade" className="mi-btn-red text-xs">
              {trial_expired ? 'Convert to paid' : source === 'trial' ? 'Convert to paid' : 'Choose a plan'} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
      {!paid && !isAdmin && source !== 'trial' && source !== 'comp' && (
        <div className="mt-5 pt-5 border-t mi-line">
          <div className="mi-eyebrow text-white/60 text-[10px] mb-2 flex items-center gap-1.5"><Lock className="w-3 h-3" /> HAVE A TRIAL PASS FROM MOTOR iNKS?</div>
          <div className="max-w-md"><TrialPassRedeem testIdPrefix="dash-trial" /></div>
        </div>
      )}
    </div>
  );
}
